// src/components/Navbar.tsx
import React, { useState, useEffect, Fragment, startTransition } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, Transition } from '@headlessui/react';
import { Menu as MenuIcon, X, Rocket, UserCircle, Settings, CreditCard, LogOut, Network, Loader2, Crown, BookOpen, LayoutDashboard, MessageSquare } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import toast from 'react-hot-toast';

import LanguageSwitcher from './LanguageSwitcher';
import { Button } from './common/Button';
import { useAuth } from '../hooks/useAuth';
import { ApiError } from '../api/apiClient';
import { openStripeBillingPortal } from '../api/billingService';
import { prefetchRoute } from '../utils/prefetchRoutes';
import { normalizeUserPlan } from '../utils/userPlan';
import { AGENTIC_DEMO_CHANGE_EVENT, readDemoOverride } from '../utils/agenticAccess';
import { DASHBOARD_NAV_DESTINATIONS } from '../config/dashboardNav.js';

/** Icons for shared dashboard destinations (Navbar account menu only). */
const DASHBOARD_NAV_ICONS: Record<string, LucideIcon> = {
    projects: Network,
    portfolio: LayoutDashboard,
    account: Settings,
    patterns: BookOpen,
    desk: MessageSquare,
};

interface NavbarProps {
    isAuthenticated: boolean;
    onLogin: () => void;
    onLogout: () => void;
}

/** Primary top-nav destinations (job entry + process + monetization + Insights). Contact stays footer-only. */
const PRIMARY_NAV = [
    { to: '/solutions', labelKey: 'nav.solutions' as const },
    { to: '/how-it-works', labelKey: 'nav.howItWorks' as const },
    { to: '/why-us', labelKey: 'nav.whyUs' as const },
    { to: '/pricing', labelKey: 'nav.pricing' as const },
    { to: '/faq', labelKey: 'nav.faq' as const },
    { to: '/blog', labelKey: 'nav.blog' as const },
] as const;

const Navbar: React.FC<NavbarProps> = ({ isAuthenticated, onLogin, onLogout }) => {
    const { t } = useTranslation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const { getAccessToken, userProfile, loading: authLoading } = useAuth();
    const [isPortalLoading, setIsPortalLoading] = useState(false);
    const [demoOverride, setDemoOverride] = useState(() => readDemoOverride());

    useEffect(() => {
        const syncDemo = () => setDemoOverride(readDemoOverride());
        window.addEventListener('storage', syncDemo);
        window.addEventListener(AGENTIC_DEMO_CHANGE_EVENT, syncDemo);
        return () => {
            window.removeEventListener('storage', syncDemo);
            window.removeEventListener(AGENTIC_DEMO_CHANGE_EVENT, syncDemo);
        };
    }, []);

    const currentPlan = normalizeUserPlan(userProfile?.plan);

    const getPlanName = (plan: string) => {
        return t(`subscription.plans.${plan.toLowerCase()}`, plan);
    };

    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    // Close drawer on route change / Escape / desktop breakpoint.
    useEffect(() => {
        closeMobileMenu();
    }, [location.pathname]);

    useEffect(() => {
        if (!isMobileMenuOpen) return undefined;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeMobileMenu();
        };
        const onResize = () => {
            if (window.matchMedia('(min-width: 1280px)').matches) closeMobileMenu();
        };

        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('resize', onResize);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('resize', onResize);
            document.body.style.overflow = prevOverflow;
        };
    }, [isMobileMenuOpen]);

    const handleManageBilling = async () => {
        try {
            setIsPortalLoading(true);
            const token = await getAccessToken();
            if (!token) throw new Error('Not authenticated');
            const { url } = await openStripeBillingPortal(token);
            window.location.href = url;
        } catch (error: unknown) {
            console.error('Portal Error:', error);
            const status = error instanceof ApiError ? error.status : (error as { status?: number })?.status;
            if (status === 400) {
                toast.error(t('premium.no_subscription_yet'));
                navigate('/pricing');
            } else {
                toast.error(t('errors.generic_fetch_error', 'Błąd połączenia.'));
            }
        } finally {
            setIsPortalLoading(false);
            closeMobileMenu();
        }
    };

    const handleStartWizard = () => {
        closeMobileMenu();
        prefetchRoute('start');
        startTransition(() => {
            navigate('/start');
        });
    };

    const getLinkClass = (isActive: boolean) =>
        `text-sm 2xl:text-base font-medium transition-colors duration-200 whitespace-nowrap ${isActive ? 'text-primary-light' : 'text-gray-300 hover:text-white'}`;

    const getMobileLinkClass = (isActive: boolean) =>
        `block px-4 py-3 text-base font-medium rounded-md transition-colors ${isActive ? 'bg-primary/10 text-primary-light' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`;

    const startButton = (
        <Button
            variant="primary"
            size="sm"
            onClick={handleStartWizard}
            onMouseEnter={() => prefetchRoute('start')}
            onFocus={() => prefetchRoute('start')}
            icon={<Rocket />}
        >
            {t('nav.startWizard')}
        </Button>
    );

    return (
        <header className="w-full bg-dark-surface sticky top-0 z-50 border-b border-dark-border shadow-lg shadow-black/20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 sm:h-20 gap-3">

                    <div className="flex-shrink-0 flex items-center min-w-0">
                        <NavLink
                            to="/"
                            className="group flex items-center gap-3"
                            onClick={closeMobileMenu}
                        >
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-blue-600 flex items-center justify-center shadow-lg shadow-green-900/20 group-hover:shadow-green-500/30 transition-all duration-300">
                                <span className="text-white font-bold text-lg leading-none tracking-tight font-poppins">
                                    aiq
                                </span>
                            </div>
                            <span className="text-white text-xl font-bold tracking-tight group-hover:text-gray-200 transition-colors whitespace-nowrap font-poppins">
                                Architect<span className="text-primary-light">-IQ</span>
                            </span>
                        </NavLink>
                    </div>

                    {/* Desktop primary nav */}
                    <div className="hidden lg:flex items-center gap-2 xl:gap-3 2xl:gap-4 min-w-0">
                        <nav className="flex space-x-3 2xl:space-x-5 min-w-0" aria-label={t('nav.primary', 'Primary')}>
                            {PRIMARY_NAV.map(({ to, labelKey }) => (
                                <NavLink key={to} to={to} className={({ isActive }) => getLinkClass(isActive)}>
                                    {t(labelKey)}
                                </NavLink>
                            ))}
                        </nav>

                        <div className="h-8 w-px bg-gray-700 mx-2" />

                        <div className="flex items-center space-x-4">
                            <LanguageSwitcher />

                            {authLoading ? (
                                <div
                                    className="w-10 h-10 rounded-full bg-white/10 border border-white/10 animate-pulse"
                                    aria-busy="true"
                                    aria-label={t('nav.auth_resolving', 'Checking sign-in…')}
                                    data-testid="nav-auth-placeholder"
                                />
                            ) : isAuthenticated ? (
                                <Menu as="div" className="relative inline-block text-left">
                                    <Menu.Button
                                        className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-800 border border-gray-700 hover:border-primary/50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        aria-label={t('nav.account_menu', 'Account menu')}
                                    >
                                        <UserCircle className="text-2xl text-gray-400 hover:text-white transition-colors" />
                                    </Menu.Button>
                                    <Transition
                                        as={Fragment}
                                        enter="transition ease-out duration-100"
                                        enterFrom="transform opacity-0 scale-95"
                                        enterTo="transform opacity-100 scale-100"
                                        leave="transition ease-in duration-75"
                                        leaveFrom="transform opacity-100 scale-100"
                                        leaveTo="transform opacity-0 scale-95"
                                    >
                                        <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right divide-y divide-gray-700/50 rounded-xl bg-gray-800 shadow-xl border border-gray-700 ring-1 ring-black ring-opacity-5 focus:outline-none">

                                            <div className="px-4 py-3 border-b border-gray-700/50">
                                                <p className="text-xs text-gray-400 mb-1">{t('dashboard.current_plan', 'Twój Plan')}</p>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-sm font-bold ${currentPlan === 'Pro' ? 'text-yellow-400' :
                                                        currentPlan === 'Enterprise' ? 'text-blue-400' :
                                                            'text-gray-200'
                                                        }`}>
                                                        {getPlanName(currentPlan)}
                                                    </span>
                                                    {currentPlan === 'Pro' && <Crown className="text-yellow-400 text-xs" />}
                                                </div>
                                                {demoOverride && (
                                                    <p className="text-[10px] text-amber-300 mt-1 leading-snug">
                                                        {t('agentic.access.demo_navbar_preview', {
                                                            tier: demoOverride,
                                                        })}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="px-1 py-1">
                                                {DASHBOARD_NAV_DESTINATIONS.map((item) => {
                                                    const Icon = DASHBOARD_NAV_ICONS[item.id] || Settings;
                                                    return (
                                                        <Menu.Item key={item.id}>
                                                            {({ active }) => (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => navigate(item.to)}
                                                                    className={`${active ? 'bg-primary/20 text-primary-light' : 'text-gray-300'} group flex w-full items-center rounded-md px-3 py-2.5 text-sm gap-3 transition-colors`}
                                                                >
                                                                    <Icon className="text-gray-400 group-hover:text-primary-light" />
                                                                    {t(item.labelKey)}
                                                                </button>
                                                            )}
                                                        </Menu.Item>
                                                    );
                                                })}
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <button
                                                            type="button"
                                                            onClick={handleManageBilling}
                                                            disabled={isPortalLoading}
                                                            className={`${active ? 'bg-indigo-600/20 text-indigo-300' : 'text-gray-300'} ${isPortalLoading ? 'opacity-50' : ''} group flex w-full items-center rounded-md px-3 py-2.5 text-sm gap-3 transition-colors`}
                                                        >
                                                            {isPortalLoading ? <Loader2 className="text-gray-400 animate-spin" /> : <CreditCard className="text-gray-400 group-hover:text-indigo-300" />}
                                                            {t('dashboard.nav.billing')}
                                                        </button>
                                                    )}
                                                </Menu.Item>
                                            </div>
                                            <div className="px-1 py-1">
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <button
                                                            type="button"
                                                            onClick={onLogout}
                                                            className={`${active ? 'bg-red-500/20 text-red-400' : 'text-gray-300'} group flex w-full items-center rounded-md px-3 py-2.5 text-sm gap-3 transition-colors`}
                                                        >
                                                            <LogOut className="text-gray-400 group-hover:text-red-400" />
                                                            {t('nav.logout', 'Wyloguj się')}
                                                        </button>
                                                    )}
                                                </Menu.Item>
                                            </div>
                                        </Menu.Items>
                                    </Transition>
                                </Menu>
                            ) : (
                                <Button
                                    variant="glass"
                                    size="sm"
                                    onClick={onLogin}
                                    className="border-white/20 hover:border-white/40"
                                >
                                    {t('nav.login')}
                                </Button>
                            )}

                            <Button
                                variant="primary"
                                onClick={handleStartWizard}
                                onMouseEnter={() => prefetchRoute('start')}
                                onFocus={() => prefetchRoute('start')}
                                icon={<Rocket />}
                            >
                                {t('nav.startWizard')}
                            </Button>
                        </div>
                    </div>

                    {/* Tablet / mobile: Start always visible + menu */}
                    <div className="lg:hidden flex items-center gap-2 sm:gap-3 flex-shrink-0">
                        {startButton}
                        <LanguageSwitcher />
                        <button
                            type="button"
                            onClick={() => setIsMobileMenuOpen((open) => !open)}
                            className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary transition-colors"
                            aria-expanded={isMobileMenuOpen}
                            aria-controls="mobile-primary-nav"
                            aria-label={isMobileMenuOpen ? t('nav.close_menu', 'Close menu') : t('nav.open_menu', 'Open menu')}
                        >
                            {isMobileMenuOpen ? (
                                <X className="block h-7 w-7" />
                            ) : (
                                <MenuIcon className="block h-7 w-7" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {isMobileMenuOpen && (
                <div
                    id="mobile-primary-nav"
                    className="lg:hidden bg-dark-surface border-b border-dark-border animate-fade-in shadow-2xl"
                >
                    <div className="px-4 pt-4 pb-6 space-y-2">
                        <nav aria-label={t('nav.primary', 'Primary')}>
                            {PRIMARY_NAV.map(({ to, labelKey }) => (
                                <NavLink
                                    key={to}
                                    to={to}
                                    onClick={closeMobileMenu}
                                    className={({ isActive }) => getMobileLinkClass(isActive)}
                                >
                                    {t(labelKey)}
                                </NavLink>
                            ))}
                        </nav>

                        <div className="border-t border-gray-700 my-4 pt-4 flex flex-col gap-3">
                            {authLoading ? (
                                <div
                                    className="h-11 w-full rounded-xl bg-white/10 border border-white/10 animate-pulse"
                                    aria-busy="true"
                                    aria-label={t('nav.auth_resolving', 'Checking sign-in…')}
                                    data-testid="nav-auth-placeholder-mobile"
                                />
                            ) : isAuthenticated ? (
                                <div className="flex flex-col gap-2 mb-2 bg-gray-800/50 p-2 rounded-xl">
                                    <div className="px-4 py-2 mb-2 border-b border-gray-700/50">
                                        <p className="text-xs text-gray-400">{t('dashboard.current_plan', 'Twój Plan')}: <span className="font-bold text-white">{getPlanName(currentPlan)}</span></p>
                                        {demoOverride && (
                                            <p className="text-[10px] text-amber-300 mt-1 leading-snug">
                                                {t('agentic.access.demo_navbar_preview', { tier: demoOverride })}
                                            </p>
                                        )}
                                    </div>
                                    {DASHBOARD_NAV_DESTINATIONS.map((item) => {
                                        const Icon = DASHBOARD_NAV_ICONS[item.id] || Settings;
                                        return (
                                            <button
                                                key={item.id}
                                                type="button"
                                                onClick={() => { closeMobileMenu(); navigate(item.to); }}
                                                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
                                            >
                                                <Icon className="text-lg text-gray-400" />
                                                {t(item.labelKey)}
                                            </button>
                                        );
                                    })}

                                    <button
                                        type="button"
                                        onClick={handleManageBilling}
                                        disabled={isPortalLoading}
                                        className={`flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors ${isPortalLoading ? 'opacity-50' : ''}`}
                                    >
                                        {isPortalLoading ? <Loader2 className="text-lg animate-spin" /> : <CreditCard className="text-lg text-gray-400" />}
                                        {t('dashboard.nav.billing')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { onLogout(); closeMobileMenu(); }}
                                        className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-md transition-colors"
                                    >
                                        <LogOut className="text-lg" /> {t('nav.logout', 'Wyloguj się')}
                                    </button>
                                </div>
                            ) : (
                                <Button
                                    variant="secondary"
                                    onClick={() => { onLogin(); closeMobileMenu(); }}
                                    className="w-full justify-center"
                                >
                                    {t('nav.login')}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
